import React, { useState, useEffect, Fragment } from 'react';
import * as XLSX from 'xlsx';
import { FaTimes, FaUpload, FaFileExcel, FaSearch, FaSpinner, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { DataGrid } from '@mui/x-data-grid';
import { Tab } from '@headlessui/react';
import { supabase } from './supabaseClient';
import { toast } from 'react-toastify';

const CallLogUploader = ({ session, onCancel }) => {
  const [rows, setRows] = useState([]);
  const [rawJsonData, setRawJsonData] = useState([]); // Datos originales del Excel sin transformar
  const [columns, setColumns] = useState([]);
  const [uploadedRows, setUploadedRows] = useState([]);
  const [uploadedColumns, setUploadedColumns] = useState([]);
  const [fileName, setFileName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dbColumns, setDbColumns] = useState([]);
  const [isSchemaLoading, setIsSchemaLoading] = useState(true);
  const [validationResult, setValidationResult] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [schemaError, setSchemaError] = useState(null);

  const fetchDbColumns = async () => {
    setIsSchemaLoading(true);
    setSchemaError(null);
    try {
      const { data, error } = await supabase.rpc('get_staging_columns');
      if (error) {
        throw error;
      }
      // Filtramos columnas de sistema que no se esperan en el Excel
      const systemCols = ["id", "inserted_at", "created_at", "batch_id", "raw_payload"];
      const expectedCols = data.map(c => c.column_name).filter(c => !systemCols.includes(c));
      setDbColumns(expectedCols);
    } catch (error) {
      console.error("Error fetching schema:", error);
      setSchemaError(error.message);
      toast.error(`Error al obtener definición de tabla: ${error.message}`);
    } finally {
      setIsSchemaLoading(false);
    }
  };

  const hasValidationErrors = validationResult.some(item => item.status === 'error');
  // El botón Cargar solo se habilita si hay validación ejecutada y no hay errores
  const canUpload = validationResult.length > 0 && !hasValidationErrors;

  // Obtener columnas esperadas de la BD al montar el componente
  useEffect(() => {
    fetchDbColumns();
  }, []);

  const handleFileChange = (e) => {
    setUploadError(null); // Limpiar errores previos al cargar nuevo archivo
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (dbColumns.length === 0) {
        toast.warn("El esquema de la base de datos no está cargado. La validación puede ser incorrecta. Intente de nuevo.");
        // Reset file input so user can try again
        e.target.value = null;
        setFileName('');
        return;
      }

      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      // defval: '' garantiza que todas las columnas del header aparezcan en cada fila,
      // incluso si tienen celdas vacías, evitando que columnas se pierdan.
      const jsonData = XLSX.utils.sheet_to_json(ws, { defval: '' });

      // Extraer columnas directamente del header de la hoja (fila 1) para no depender
      // de que la primera fila de datos tenga todas las celdas llenas.
      const headerRow = XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || [];
      const excelColsFromHeader = headerRow.map(c => c ? c.toString().trim() : '').filter(c => c !== '');

      if (jsonData.length > 0) {
        // Usar columnas del header en lugar de Object.keys(jsonData[0])
        const excelCols = excelColsFromHeader.length > 0 ? excelColsFromHeader : Object.keys(jsonData[0]);
        const normalize = (str) => str ? str.toString().trim().toLowerCase() : '';

        const excelNormToOrig = new Map(excelCols.map(c => [normalize(c), c]));
        const dbNormToOrig = new Map(dbColumns.map(c => [normalize(c), c]));

        const allNormCols = new Set([...excelNormToOrig.keys(), ...dbNormToOrig.keys()]);

        const validation = Array.from(allNormCols).map(normCol => {
            const inExcel = excelNormToOrig.has(normCol);
            const inDb = dbNormToOrig.has(normCol);

            let status = 'ok';
            if (inDb && !inExcel) status = 'error'; // Missing from Excel
            if (inExcel && !inDb) status = 'error'; // Extra in Excel, Mark as error

            return {
                excelCol: inExcel ? excelNormToOrig.get(normCol) : '(No en Excel)',
                dbCol: inDb ? dbNormToOrig.get(normCol) : '(No en BD)',
                status: status
            };
        });
        
        // Sort for consistent display: errors, warnings, then ok
         validation.sort((a, b) => {
            const statusOrder = { 'error': 0, 'warning': 1, 'ok': 2 };
            return statusOrder[a.status] - statusOrder[b.status];
        });

        setValidationResult(validation);
        
        // Crear un mapa de transformación de claves: Nombre Excel Original -> Nombre BD Original
        const keyMap = {};
        for (const [normCol, origExcelCol] of excelNormToOrig.entries()) {
            if (dbNormToOrig.has(normCol)) {
                // Found a match. Map original Excel name to original DB name.
                keyMap[origExcelCol] = dbNormToOrig.get(normCol);
            }
        }

        // Transformar los datos para usar las claves de la BD (para que el backend reciba lo correcto)
        const transformedRows = jsonData.map((row, index) => {
            const newRow = { id: index };
            Object.keys(row).forEach(key => {
                // If the key has a mapping, use the DB key. Otherwise, keep the original (for extra columns).
                const finalKey = keyMap[key] || key;
                newRow[finalKey] = row[key];
            });
            return newRow;
        });

        // Definir columnas para el DataGrid usando TODAS las columnas del Excel (mapeadas a BD)
        // Esto garantiza que todas las columnas aparezcan aunque la primera fila tenga celdas vacías.
        const cols = excelCols.map(origExcelCol => {
          const finalKey = keyMap[origExcelCol] || origExcelCol;
          return {
            field: finalKey,
            headerName: finalKey,
            flex: 1,
            minWidth: 150,
          };
        });

        setColumns(cols);
        setRows(transformedRows);
        setRawJsonData(jsonData); // Guardar datos originales del Excel para Vista JSON
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleCancel = () => {
    setRows([]);
    setRawJsonData([]);
    setColumns([]);
    setUploadedRows([]);
    setUploadedColumns([]);
    setFileName('');
    setSearchTerm('');
    setUploadError(null);
    setIsUploaded(false);
    setSelectedIndex(0);
    setValidationResult([]);
    setBatchId(null);
    // Llama a la función onCancel pasada por props para volver a la pantalla principal
    if (onCancel) {
      onCancel();
    }
  };

  const handleUpload = async () => {
    try {
      setIsUploading(true);
      setUploadError(null);

      // 1. Crear el registro maestro en mvw_call_upload_batches
      const insertPayload = {
        file_name: fileName,
        uploaded_by: session.user.email,
        records_processed: rows.length
      };
      
      console.log("Intentando insertar en mvw_raw.mvw_call_upload_batches. Payload:", insertPayload);

      const { data: batchData, error: batchError } = await supabase
        .schema('mvw_raw')
        .from('mvw_call_upload_batches')
        .insert(insertPayload)
        .select()
        .single();

      if (batchError) {
        console.error("Error DETALLADO de Supabase al insertar lote:", batchError);
        throw new Error(`Error al crear lote: ${batchError.message} (Código: ${batchError.code || 'Desconocido'}) - Revisa la consola (F12) para más detalles.`);
      }
      
      const newBatchId = batchData.id;
      setBatchId(newBatchId);

      // 2. Preparamos los registros para subir
      const recordsToUpload = rows.map(({ id, ...rest }) => rest);

      // 3. Insertar registros directamente desde React (en lotes)
      // Esto es más robusto que la Edge Function para cargas directas y evita problemas de CORS
      const CHUNK_SIZE = 1000; // Insertamos de 1000 en 1000
      
      for (let i = 0; i < recordsToUpload.length; i += CHUNK_SIZE) {
        const chunk = recordsToUpload.slice(i, i + CHUNK_SIZE).map(record => ({
          ...record,
          batch_id: newBatchId, // Asignamos el ID del lote a cada registro
          // uploaded_by: session.user.email // Descomentar si la tabla staging tiene esta columna
        }));

        const { error: insertError } = await supabase
          .schema('mvw_raw')
          .from('mvw_calls_staging')
          .insert(chunk);

        if (insertError) {
          console.error("Error insertando lote:", insertError);
          throw new Error(`Error al insertar registros (lote ${Math.floor(i/CHUNK_SIZE) + 1}): ${insertError.message}`);
        }
      }

      // 4. Verificar carga consultando la base de datos
      
      const { data: dbRows, error: dbError, count: insertedCount } = await supabase
        .schema('mvw_raw')
        .from('mvw_calls_staging')
        .select('*', { count: 'exact', head: false })
        .eq('batch_id', newBatchId);

      if (dbError) {
        throw new Error(`Carga realizada, pero error al verificar registros: ${dbError.message}`);
      }

      // 5. Actualizar el conteo de registros insertados en la tabla maestra
      const { error: updateError } = await supabase
        .schema('mvw_raw')
        .from('mvw_call_upload_batches')
        .update({ records_inserted: insertedCount || 0 })
        .eq('id', newBatchId);

      if (updateError) {
        // No es un error crítico, pero es bueno notificarlo.
        toast.warn(`No se pudo actualizar el conteo final del lote: ${updateError.message}`);
      }

      if (dbRows && dbRows.length > 0) {
        // Generar columnas dinámicas para la vista de base de datos
        const firstRow = dbRows[0];
        const cols = Object.keys(firstRow).map((key) => ({
          field: key,
          headerName: key,
          flex: 1,
          minWidth: 150,
        }));
        setUploadedColumns(cols);
        setUploadedRows(dbRows);
      }

      setIsUploaded(true);
      setSelectedIndex(2); // Cambiar automáticamente a la tercera pestaña
      toast.success(`¡Éxito! Lote ${newBatchId} cargado con ${insertedCount} de ${rows.length} registros.`);

    } catch (err) {
      console.error("Error en handleUpload:", err);
      const msg = err.message || "Ocurrió un error desconocido durante la carga.";
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  };

  // Filtramos las filas para el buscador global (Vista Previa)
  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;
    return Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Filtramos las filas para el buscador global (Vista Base de Datos)
  const filteredUploadedRows = uploadedRows.filter((row) => {
    if (!searchTerm) return true;
    return Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="form-card">
      <h2>Cargar Logs de Llamadas</h2>
      <p className="form-description">
        En este momento Usted cargará el archivo detalle de llamadas, por favor seleccione el archivo.
      </p>

      {!fileName && (
        <>
          {schemaError ? (
            <div style={{ textAlign: 'center', color: '#721c24', marginBottom: '1rem' }}>
              <p>Error cargando esquema: {schemaError}</p>
              <button className="btn-primary" onClick={fetchDbColumns} style={{ maxWidth: '150px' }}>
                Reintentar
              </button>
            </div>
          ) : (
            <label htmlFor="file-upload" className={`btn-primary file-upload-label ${isSchemaLoading ? 'disabled' : ''}`}>
              {isSchemaLoading ? (
                <>
                  <FaSpinner className="spinner" style={{ marginRight: '8px' }} />
                  Cargando esquema...
                </>
              ) : (
                <>
                  <FaFileExcel style={{ marginRight: '8px' }} />
                  Seleccionar Archivo Excel
                </>
              )}
            </label>
          )}
        </>
      )}
      <input id="file-upload" type="file" onChange={handleFileChange} accept=".xlsx, .xls" disabled={isSchemaLoading || !!schemaError} />

      {fileName && (
        <>
          <div className="file-info-card">
            <div className="info-item">
              <span className="info-label">Archivo</span>
              <span className="info-value">{fileName}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Registros</span>
              <span className="info-value">{rows.length}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Estado</span>
              <span className="info-value">
                {isUploaded ? (
                   <span style={{ color: 'green', display: 'flex', alignItems: 'center', gap: '5px' }}><FaCheckCircle /> Cargado</span>
                ) : isUploading ? (
                   <span style={{ color: '#007bff', display: 'flex', alignItems: 'center', gap: '5px' }}><FaSpinner className="spinner"/> Procesando</span>
                ) : (
                   <span style={{ color: '#6c757d' }}>Pendiente</span>
                )}
              </span>
            </div>
            {batchId && (
              <div className="info-item">
                <span className="info-label">Batch ID</span>
                <span className="info-value highlight">#{batchId}</span>
              </div>
            )}
          </div>

          <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
            <div className="tabs-header">
              <Tab.List className="tab-list">
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`}>
                      Vista Tabla
                    </button>
                  )}
                </Tab>
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`}>
                      Vista JSON
                    </button>
                  )}
                </Tab>
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`}>
                      Validación de Columnas
                    </button>
                  )}
                </Tab>
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`} disabled={!isUploaded && uploadedRows.length === 0}>
                      Registros Cargados {batchId ? `(Lote #${batchId})` : '(BD)'}
                    </button>
                  )}
                </Tab>
              </Tab.List>

              <div className="action-buttons">
                <button onClick={handleCancel} className="btn-cancel">
                  <FaTimes style={{ marginRight: '8px' }} />
                  Cancelar
                </button>
                <button onClick={handleUpload} className="btn-confirm" disabled={isUploading || isUploaded || !canUpload}>
                  {isUploading ? (
                    <>
                      <FaSpinner className="spinner" style={{ marginRight: '8px' }} />
                      Cargando...
                    </>
                  ) : (
                    <>
                      <FaUpload style={{ marginRight: '8px' }} />
                      {isUploaded ? 'Cargado' : 'Cargar'}
                    </>
                  )}
                </button>
              </div>
            </div>

            <Tab.Panels>
              <Tab.Panel>
                <div className="search-bar-container">
                  <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input 
                      type="text" 
                      placeholder="Buscar..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>
                <div className="grid-container">
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    density="compact" // Para el look minimalista
                    sx={{ border: 0 }}
                  />
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div className="json-viewer">
                  {/* Mostrar los datos originales del Excel tal como vienen del archivo */}
                  <pre>{JSON.stringify(rawJsonData, null, 2)}</pre>
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div className="grid-container" style={{ overflowY: 'auto', padding: '1rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                        <th style={{ padding: '8px' }}>Columna Excel</th>
                        <th style={{ padding: '8px' }}>Columna Base de Datos (Esperada)</th>
                        <th style={{ padding: '8px', textAlign: 'center' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.map((item, idx) => (
                        <tr key={idx} style={{ 
                            borderBottom: '1px solid #f0f0f0', 
                            backgroundColor: item.status === 'error' ? '#fff5f5' : (item.status === 'warning' ? '#fffbeb' : 'transparent') 
                          }}>
                          <td style={{ padding: '8px', fontWeight: item.status !== 'ok' ? 'bold' : 'normal' }}>{item.excelCol}</td>
                          <td style={{ padding: '8px' }}>{item.dbCol}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {item.status === 'ok' && (
                              <span style={{ color: 'green', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                <FaCheckCircle /> OK
                              </span>
                            )}
                            {item.status === 'error' && (
                              <span style={{ color: 'red', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                <FaExclamationCircle /> Error
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div className="search-bar-container">
                  <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input 
                      type="text" 
                      placeholder="Buscar en BD..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>
                <div className="grid-container">
                  <DataGrid
                    rows={filteredUploadedRows}
                    columns={uploadedColumns}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    density="compact"
                    sx={{ border: 0 }}
                  />
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>

          {uploadError && <p className="error-message" style={{ marginTop: '1rem' }}>{uploadError}</p>}
        </>
      )}
    </div>
  );
};

export default CallLogUploader;